<template>
  <div class="config-container" :class="{ 'dark-mode': isDarkMode }">
    <el-card class="config-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-setting"></i> {{ pageTitle }}</span>
        <div class="header-actions">
          <el-button type="primary" size="small" @click="saveConfig" :loading="saving">
            <i class="el-icon-check"></i> 保存配置
          </el-button>
          <el-button size="small" @click="loadConfig">
            <i class="el-icon-refresh"></i> 重置
          </el-button>
        </div>
      </div>

      <el-form :model="configForm" label-width="180px" class="config-form">
        <el-form-item label="主题模式">
          <el-radio-group v-model="configForm.adminThemeMode" @change="handleThemeModeChange">
            <el-radio label="light">
              <i class="el-icon-sunny"></i> 浅色模式
            </el-radio>
            <el-radio label="dark">
              <i class="el-icon-moon"></i> 深色模式
            </el-radio>
          </el-radio-group>
          <div class="tip">设置管理后台的整体主题模式，深色模式适合夜间使用</div>
        </el-form-item>

        <el-form-item label="快速应用主题预设">
          <el-button-group>
            <el-button size="small" @click="applyPreset('default')">默认浅色</el-button>
            <el-button size="small" @click="applyPreset('blue')">蓝色风格</el-button>
            <el-button size="small" @click="applyPreset('dark')">经典深色</el-button>
            <el-button size="small" @click="applyPreset('darkBlue')">深蓝风格</el-button>
          </el-button-group>
          <div class="tip">快速应用预设配色方案，点击后会自动填充下方颜色配置</div>
        </el-form-item>

        <el-divider content-position="left">侧边栏配置</el-divider>

        <el-form-item label="侧边栏背景颜色">
          <el-color-picker 
            v-model="configForm.adminSidebarBgColor" 
            show-alpha
            :predefine="predefineColors">
          </el-color-picker>
          <span class="color-preview">{{ configForm.adminSidebarBgColor || '#304156' }}</span>
          <div class="tip">设置左侧菜单栏的背景颜色</div>
        </el-form-item>

        <el-form-item label="侧边栏文字颜色">
          <el-color-picker 
            v-model="configForm.adminSidebarTextColor" 
            show-alpha
            :predefine="predefineColors">
          </el-color-picker>
          <span class="color-preview">{{ configForm.adminSidebarTextColor || '#bfcbd9' }}</span>
          <div class="tip">设置左侧菜单栏的文字颜色</div>
        </el-form-item>

        <el-form-item label="侧边栏激活颜色">
          <el-color-picker 
            v-model="configForm.adminSidebarActiveColor" 
            show-alpha
            :predefine="predefineColors">
          </el-color-picker>
          <span class="color-preview">{{ configForm.adminSidebarActiveColor || '#409EFF' }}</span>
          <div class="tip">设置左侧菜单激活项的高亮颜色</div>
        </el-form-item>

        <el-divider content-position="left">顶部导航栏配置</el-divider>

        <el-form-item label="顶部栏背景颜色">
          <el-color-picker 
            v-model="configForm.adminHeaderBgColor" 
            show-alpha
            :predefine="predefineColors">
          </el-color-picker>
          <span class="color-preview">{{ configForm.adminHeaderBgColor || '#ffffff' }}</span>
          <div class="tip">设置顶部导航栏的背景颜色</div>
        </el-form-item>

        <el-form-item label="顶部栏文字颜色">
          <el-color-picker 
            v-model="configForm.adminHeaderTextColor" 
            show-alpha
            :predefine="predefineColors">
          </el-color-picker>
          <span class="color-preview">{{ configForm.adminHeaderTextColor || '#303133' }}</span>
          <div class="tip">设置顶部导航栏的文字颜色</div>
        </el-form-item>

        <el-divider></el-divider>

        <!-- 预览区域 -->
        <el-form-item label="效果预览">
          <div class="preview-container">
            <div class="preview-layout">
              <div class="preview-sidebar" :style="previewSidebarStyle">
                <div class="preview-logo">管理后台</div>
                <div class="preview-menu-item" :style="previewMenuStyle">
                  <i class="el-icon-document"></i> 资源管理
                </div>
                <div class="preview-menu-item preview-menu-active" :style="previewMenuActiveStyle">
                  <i class="el-icon-setting"></i> 系统配置
                </div>
                <div class="preview-menu-item" :style="previewMenuStyle">
                  <i class="el-icon-folder"></i> 分类管理
                </div>
              </div>
              <div class="preview-main">
                <div class="preview-header" :style="previewHeaderStyle">
                  资源分享管理系统
                </div>
                <div class="preview-content">
                  <div style="padding: 20px; color: #909399;">
                    配置内容区域...
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'AdminConfig',
  data() {
    return {
      pageTitle: '后端系统配置', // 默认标题
      configForm: {
        adminThemeMode: 'light',
        adminSidebarBgColor: '#304156',
        adminSidebarTextColor: '#bfcbd9',
        adminSidebarActiveColor: '#409EFF',
        adminHeaderBgColor: '#ffffff',
        adminHeaderTextColor: '#303133'
      },
      saving: false,
      predefineColors: [
        '#304156',
        '#001529',
        '#1890ff',
        '#409EFF',
        '#67C23A',
        '#E6A23C',
        '#F56C6C',
        '#909399',
        '#ffffff',
        '#000000',
        '#bfcbd9'
      ]
    }
  },
  computed: {
    isDarkMode() {
      return this.configForm.adminThemeMode === 'dark'
    },
    previewSidebarStyle() {
      return {
        backgroundColor: this.configForm.adminSidebarBgColor || '#304156',
        color: this.configForm.adminSidebarTextColor || '#bfcbd9'
      }
    },
    previewMenuStyle() {
      return {
        color: this.configForm.adminSidebarTextColor || '#bfcbd9'
      }
    },
    previewMenuActiveStyle() {
      const activeColor = this.configForm.adminSidebarActiveColor || '#409EFF'
      // 将颜色转换为 rgba 格式，透明度设置为 0.1
      let bgColor = 'rgba(64, 158, 255, 0.1)'
      
      // 如果是十六进制颜色，转换为 rgba
      if (activeColor.startsWith('#')) {
        const r = parseInt(activeColor.slice(1, 3), 16)
        const g = parseInt(activeColor.slice(3, 5), 16)
        const b = parseInt(activeColor.slice(5, 7), 16)
        bgColor = `rgba(${r}, ${g}, ${b}, 0.1)`
      }
      
      return {
        color: activeColor,
        backgroundColor: bgColor,
        borderLeftColor: activeColor
      }
    },
    previewHeaderStyle() {
      return {
        backgroundColor: this.configForm.adminHeaderBgColor || '#ffffff',
        color: this.configForm.adminHeaderTextColor || '#303133'
      }
    }
  },
  mounted() {
    this.loadPageTitle()
    this.loadConfig()
  },
  methods: {
    // 动态加载页面标题
    async loadPageTitle() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allMenus = menus.menus || []
          const currentMenu = allMenus.find(m => m.url === this.$route.path)
          if (currentMenu) {
            this.pageTitle = currentMenu.name
          }
        }
      } catch (error) {
        console.error('加载页面标题失败:', error)
      }
    },
    async loadConfig() {
      try {
        const res = await this.$axios.get('/admin/config/admin-theme')
        if (res.data.code === 200 && res.data.data) {
          const config = res.data.data
          this.configForm.adminThemeMode = config.adminThemeMode || 'light'
          this.configForm.adminSidebarBgColor = config.adminSidebarBgColor || '#304156'
          this.configForm.adminSidebarTextColor = config.adminSidebarTextColor || '#bfcbd9'
          this.configForm.adminSidebarActiveColor = config.adminSidebarActiveColor || '#409EFF'
          this.configForm.adminHeaderBgColor = config.adminHeaderBgColor || '#ffffff'
          this.configForm.adminHeaderTextColor = config.adminHeaderTextColor || '#303133'
        }
      } catch (error) {
        console.error('加载配置失败:', error)
      }
    },
    async saveConfig() {
      this.saving = true
      try {
        await this.$axios.put('/admin/config/admin-theme', this.configForm)
        this.$message.success('配置保存成功，正在刷新页面...')
        
        // 缓存到localStorage，避免下次加载闪烁
        localStorage.setItem('admin_theme_config', JSON.stringify(this.configForm))
        
        // 触发全局事件，通知Layout更新样式
        this.$root.$emit('admin-theme-change', this.configForm)
        
        // 延迟500ms后刷新页面，让用户看到成功提示
        setTimeout(() => {
          window.location.reload()
        }, 500)
      } catch (error) {
        this.$message.error('保存失败')
        this.saving = false
      }
    },
    handleThemeModeChange(mode) {
      // 主题模式变化时的提示
      if (mode === 'dark') {
        this.$message.info('切换到深色模式，建议配合使用深色预设方案')
      }
    },
    applyPreset(preset) {
      const presets = {
        default: {
          adminThemeMode: 'light',
          adminSidebarBgColor: '#304156',
          adminSidebarTextColor: '#bfcbd9',
          adminSidebarActiveColor: '#409EFF',
          adminHeaderBgColor: '#ffffff',
          adminHeaderTextColor: '#303133'
        },
        blue: {
          adminThemeMode: 'light',
          adminSidebarBgColor: '#1890ff',
          adminSidebarTextColor: '#ffffff',
          adminSidebarActiveColor: '#ffffff',
          adminHeaderBgColor: '#1890ff',
          adminHeaderTextColor: '#ffffff'
        },
        dark: {
          adminThemeMode: 'dark',
          adminSidebarBgColor: '#001529',
          adminSidebarTextColor: '#rgba(255,255,255,0.65)',
          adminSidebarActiveColor: '#1890ff',
          adminHeaderBgColor: '#001529',
          adminHeaderTextColor: '#ffffff'
        },
        darkBlue: {
          adminThemeMode: 'dark',
          adminSidebarBgColor: '#001f3f',
          adminSidebarTextColor: '#b0c4de',
          adminSidebarActiveColor: '#4a9eff',
          adminHeaderBgColor: '#001f3f',
          adminHeaderTextColor: '#e0e0e0'
        }
      }
      
      const selectedPreset = presets[preset]
      if (selectedPreset) {
        Object.assign(this.configForm, selectedPreset)
        this.$message.success(`已应用${preset === 'default' ? '默认浅色' : preset === 'blue' ? '蓝色风格' : preset === 'dark' ? '经典深色' : '深蓝风格'}预设方案`)
      }
    }
  }
}
</script>

<style scoped>
.config-container {
  padding: 20px;
  background: transparent;
  min-height: calc(100vh - 60px);
}

.config-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.card-header i {
  margin-right: 8px;
  color: #409EFF;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.config-form {
  max-width: 800px;
}

.config-form >>> .el-form-item {
  margin-bottom: 30px;
}

.tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.color-preview {
  margin-left: 15px;
  font-family: 'Courier New', monospace;
  color: #606266;
  font-size: 13px;
}

/* 预览区域 */
.preview-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-layout {
  display: flex;
  height: 300px;
}

.preview-sidebar {
  width: 150px;
  transition: all 0.3s;
}

.preview-logo {
  padding: 15px;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.preview-menu-item {
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 13px;
}

.preview-menu-item i {
  margin-right: 8px;
}

.preview-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.preview-menu-active {
  border-left: 3px solid;
}

.preview-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
}

.preview-header {
  height: 50px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  transition: all 0.3s;
}

.preview-content {
  flex: 1;
  background: #f0f2f5;
}

/* 深色模式样式 */
.dark-mode .config-card {
  background-color: #2a2a2a;
  border-color: #404040;
}

.dark-mode .card-header {
  color: #e0e0e0;
}

.dark-mode >>> .el-divider__text {
  background-color: #2a2a2a;
  color: #b0b0b0;
}

.dark-mode >>> .el-form-item__label {
  color: #b0b0b0;
}

.dark-mode .tip {
  color: #909399;
}

.dark-mode .color-preview {
  color: #b0b0b0;
}

.dark-mode >>> .el-radio__label {
  color: #b0b0b0;
}

.dark-mode >>> .el-radio__input.is-checked + .el-radio__label {
  color: #409EFF;
}

.dark-mode .preview-container {
  border-color: #404040;
}

.dark-mode .preview-main {
  background: #1a1a1a;
}

.dark-mode .preview-content {
  background: #1a1a1a;
}
</style>
